import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { debugLogger } from '@/utils/debugLogger';
import { useMultiSizeImageProcessor } from './useMultiSizeImageProcessor';
import { generateUserHash, generatePhotoFilename } from '@/utils/photoUrlUtils';
import { findExistingPhotos, findBestSourcePhoto, generatePossibleVariantUrls } from '@/utils/photoVersionDetector';

export interface MigrationProgress {
  userId: string;
  username: string;
  status: 'pending' | 'analyzing' | 'downloading' | 'processing' | 'uploading' | 'updating' | 'success' | 'error';
  progress: number;
  message: string;
  error?: string;
}

export const usePhotoMigration = () => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<MigrationProgress[]>([]);
  const { processImageToVariants } = useMultiSizeImageProcessor();

  /**
   * Migrar um único usuário para v1
   */
  const migrateUserToV1 = useCallback(async (
    userId: string,
    username: string,
    mainPhotoUrl: string
  ): Promise<boolean> => {
    debugLogger.info('[usePhotoMigration] Starting migration:', { userId, username });

    const updateProgress = (status: MigrationProgress['status'], message: string, progressPercent: number) => {
      setProgress(prev => {
        const existing = prev.find(p => p.userId === userId);
        if (existing) {
          return prev.map(p => p.userId === userId ? { ...p, status, message, progress: progressPercent } : p);
        }
        return [...prev, { userId, username, status, message, progress: progressPercent }];
      });
    };

    try {
      // 1. ANALISAR: Encontrar todas as variantes existentes
      updateProgress('analyzing', 'Analisando fotos existentes...', 10);
      const possibleUrls = generatePossibleVariantUrls(mainPhotoUrl);
      const existingPhotos = await findExistingPhotos(possibleUrls);

      if (existingPhotos.size === 0) {
        throw new Error('Nenhuma foto encontrada');
      }

      // 2. ESCOLHER: Melhor foto fonte (maior tamanho)
      const bestSource = findBestSourcePhoto(existingPhotos);
      if (!bestSource) {
        throw new Error('Não foi possível determinar a melhor foto');
      }

      debugLogger.info('[usePhotoMigration] Best source selected:', bestSource);

      // 3. DOWNLOAD: Baixar a foto fonte
      updateProgress('downloading', 'Baixando foto fonte...', 30);
      const response = await fetch(bestSource.url);
      const blob = await response.blob();
      const sourceFile = new File([blob], 'source.jpg', { type: 'image/jpeg' });

      // 4. PROCESSAR: Gerar as 4 variantes v1
      updateProgress('processing', 'Processando variantes v1...', 50);
      const variants = await processImageToVariants(sourceFile);

      // 5. UPLOAD: Enviar as 4 variantes para o storage
      updateProgress('uploading', 'Fazendo upload das variantes...', 70);
      const userHash = await generateUserHash(userId);
      const timestamp = Date.now();
      const uploadedUrls: string[] = [];

      for (const variant of variants) {
        const filename = generatePhotoFilename(username, timestamp, variant.type);
        const path = `${userHash}/${filename}`;

        const { error: uploadError } = await supabase.storage
          .from('profile_photos')
          .upload(path, variant.file, {
            contentType: 'image/jpeg',
            upsert: true
          });

        if (uploadError) {
          throw new Error(`Erro ao fazer upload de ${variant.type}: ${uploadError.message}`);
        }

        const { data: { publicUrl } } = supabase.storage
          .from('profile_photos')
          .getPublicUrl(path);

        uploadedUrls.push(publicUrl);
        debugLogger.debug('[usePhotoMigration] Uploaded variant:', { type: variant.type, url: publicUrl });
      }

      // 6. ATUALIZAR: Atualizar o banco de dados
      updateProgress('updating', 'Atualizando banco de dados...', 90);

      // URL base (sem variante)
      const baseUrl = uploadedUrls[0].replace(/-micro-avatar\.jpg$/, '.jpg');

      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          main_photo_url: baseUrl,
          profile_photos: [baseUrl],
          photo_version: 'v1',
          updated_at: new Date().toISOString()
        })
        .eq('id', userId);

      if (updateError) {
        throw new Error(`Erro ao atualizar perfil: ${updateError.message}`);
      }

      updateProgress('success', 'Migração concluída!', 100);
      debugLogger.info('[usePhotoMigration] Migration successful:', { userId, baseUrl });
      return true;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Erro desconhecido';
      updateProgress('error', errorMessage, 0);
      debugLogger.error('[usePhotoMigration] Migration failed:', { userId, error });
      return false;
    }
  }, [processImageToVariants]);

  /**
   * Migrar múltiplos usuários em lote
   */
  const migrateBatch = useCallback(async (
    users: Array<{ id: string; username: string; main_photo_url: string }>
  ): Promise<{ success: number; failed: number }> => {
    setIsProcessing(true);
    setProgress([]);

    let success = 0;
    let failed = 0;

    // Processar em batches de 3 por vez (não sobrecarregar)
    const batchSize = 3;
    for (let i = 0; i < users.length; i += batchSize) {
      const batch = users.slice(i, i + batchSize);
      const results = await Promise.all(
        batch.map(user => migrateUserToV1(user.id, user.username, user.main_photo_url))
      );

      results.forEach(result => {
        if (result) success++;
        else failed++;
      });
    }

    setIsProcessing(false);
    debugLogger.info('[usePhotoMigration] Batch complete:', { success, failed });
    return { success, failed };
  }, [migrateUserToV1]);

  /**
   * Limpar progresso
   */
  const clearProgress = useCallback(() => {
    setProgress([]);
  }, []);

  return {
    isProcessing,
    progress,
    migrateUserToV1,
    migrateBatch,
    clearProgress
  };
};
